import { normalizeOrderFromWire } from '../components/editors/shared/data-tab/chart-data-order.util';
import type { CardLinkedFilter } from '../models/card-model';
import type { FilterConfig } from '../models/chart-config.model';
import { Aggregation, isAggregation } from '../models/chart-config.model';

/**
 * **Card `data` ↔ AppSync `CardDataConfigInput` wire helpers.**
 *
 * Owns sanitization for `updateCard`, runtime-key stripping, and **`data.filters`**
 * read/write (normalize from `getItem.card`, serialize for mutations, AI `generateDataFilter`).
 *
 * Editor UI for filters (`RuleSet`, chip labels, field lists) lives in
 * `chart-data-filter-bridge.util.ts` — do not add UI helpers here.
 *
 * AppSync allow-list (see `aws appsync get-type` / Docs audit).
 * Runtime fetch echoes (`limit`, `offset`, `total`, `result`, …) must not be sent on `updateCard`.
 */
const CARD_DATA_RUNTIME_ONLY_KEYS = [
	'limit',
	'offset',
	'total',
	'result',
	'pagination',
	/** Legacy plural shape; charts strip on write — tables may still carry it in editor state. */
	'timeDimensions',
] as const;

const CARD_DATA_CLIENT_ONLY_KEYS = [
	'selected',
	'searchLabel',
	'placeholder',
] as const;

/**
 * When AppSync `CardDataConfigInput` gains `selected: [String]`, set to `true` and
 * stop mapping `data.selected` → JSON.stringify `data.filters` on dropdown filter saves.
 */
const DROPDOWN_FILTER_GQL_PERSIST_VIA_SELECTED = false;

const TIME_DIMENSION_RUNTIME_KEYS = ['aggregation', 'dataType'] as const;

const METRIC_DIMENSION_INPUT_KEYS = new Set([
	'id',
	'name',
	'aggregation',
	'dataType',
]);

function sanitizeMetricDimensionForGql(
	item: unknown
): Record<string, unknown> | unknown {
	if (!item || typeof item !== 'object' || Array.isArray(item)) {
		return item;
	}
	const record = item as Record<string, unknown>;
	const out: Record<string, unknown> = {};
	for (const key of METRIC_DIMENSION_INPUT_KEYS) {
		if (record[key] === undefined) {
			continue;
		}
		if (key === 'aggregation') {
			const value = record[key];
			if (value === undefined || value === null) {
				continue;
			}
			const normalized =
				typeof value === 'string' ? value.toUpperCase() : value;
			if (!isAggregation(normalized)) {
				const metricName =
					typeof record.name === 'string' ? record.name : 'metric';
				throw new Error(
					`Card metric "${metricName}" has invalid aggregation for GQL: ${String(value)}`
				);
			}
			out.aggregation = normalized;
			continue;
		}
		out[key] = record[key];
	}
	return out;
}

/** getItem.card can return AWSJSON `values` as a JSON string of the array. */
function unwrapJsonEncodedArray(parsed: unknown): unknown {
	if (typeof parsed !== 'string' || !parsed.trim().startsWith('[')) {
		return parsed;
	}
	try {
		return JSON.parse(parsed);
	} catch {
		return parsed;
	}
}

function parseFilterValuesAwsJson(values: unknown): string[] {
	if (Array.isArray(values)) {
		return values.map(v => String(v));
	}
	if (typeof values === 'string') {
		const trimmed = values.trim();
		if (!trimmed) {
			return [];
		}
		try {
			const parsed = unwrapJsonEncodedArray(JSON.parse(trimmed));
			if (Array.isArray(parsed)) {
				return parsed.map(v => String(v));
			}
			if (parsed !== null && parsed !== undefined) {
				return [String(parsed)];
			}
			return [];
		} catch {
			return trimmed ? [trimmed] : [];
		}
	}
	return [];
}

/** Coerces `CardDataConfigFilter.values` (AWSJSON wire) into editor `FilterConfig.values`. */
function normalizeFilterValuesFromApi(values: unknown): (string | number)[] {
	return parseFilterValuesAwsJson(values);
}

/** Normalizes `getItem.card` `data.filters` for editor state and filter badge count. */
export function normalizeCardDataFiltersFromApi(
	filters: unknown
): FilterConfig[] | undefined {
	if (!Array.isArray(filters) || filters.length === 0) {
		return undefined;
	}
	const out: FilterConfig[] = [];
	for (const row of filters) {
		if (!row || typeof row !== 'object') {
			continue;
		}
		const record = row as Record<string, unknown>;
		const member = String(record.member ?? '').trim();
		const operator = String(record.operator ?? '').trim();
		if (!member || !operator) {
			continue;
		}
		out.push({
			member,
			operator: operator as FilterConfig['operator'],
			values: normalizeFilterValuesFromApi(record.values),
		});
	}
	return out.length > 0 ? out : undefined;
}

/**
 * Merges persisted `data` fields from `getItem.card` into existing card `data`
 * without overwriting runtime query slices (metrics, dimensions, limit, …).
 */
export function mergeCardDataFiltersFromApi(
	existing: Record<string, unknown> | undefined,
	incoming: Record<string, unknown> | undefined
): Record<string, unknown> | undefined {
	if (!incoming) {
		return existing;
	}
	const hasFilters = 'filters' in incoming;
	const hasOrder = 'order' in incoming;
	if (!hasFilters && !hasOrder) {
		return existing;
	}
	const base = { ...(existing ?? {}) };
	if (hasFilters) {
		const filters = normalizeCardDataFiltersFromApi(incoming.filters);
		if (filters?.length) {
			base.filters = filters;
		} else {
			delete base.filters;
		}
	}
	if (hasOrder) {
		const order = normalizeOrderFromWire(incoming.order);
		if (order) {
			base.order = order;
		} else {
			delete base.order;
		}
	}
	return base;
}

/** Maps AppSync `CardItem.filters` → `linkedFilters` on editor card config. */
function normalizeCardLinkedFiltersFromApi(
	filters: unknown
): CardLinkedFilter[] | undefined {
	if (!Array.isArray(filters) || filters.length === 0) {
		return undefined;
	}
	const out: CardLinkedFilter[] = [];
	for (const row of filters) {
		if (!row || typeof row !== 'object') {
			continue;
		}
		const record = row as Record<string, unknown>;
		const filterId = String(record.filterId ?? record.id ?? '').trim();
		const label = String(record.label ?? '').trim();
		if (!filterId || !label) {
			continue;
		}
		out.push({
			filterId,
			label,
			isGlobal:
				typeof record.isGlobal === 'boolean'
					? record.isGlobal
					: undefined,
			filter: record.filter,
		});
	}
	return out.length > 0 ? out : undefined;
}

/** Normalizes `getItem.card` linked filters and `data.filters` for editor state. */
export function applyCardDataWireFromApi(
	card: Record<string, unknown> | undefined
): void {
	if (!card || typeof card !== 'object') {
		return;
	}
	const linkedFilters = normalizeCardLinkedFiltersFromApi(card.filters);
	if (linkedFilters) {
		card.linkedFilters = linkedFilters;
	} else {
		delete card.linkedFilters;
	}
	delete card.filters;

	const data = card.data;
	if (!data || typeof data !== 'object' || Array.isArray(data)) {
		return;
	}
	const record = data as Record<string, unknown>;
	const filters = normalizeCardDataFiltersFromApi(record.filters);
	if (filters) {
		record.filters = filters;
	} else {
		delete record.filters;
	}
	const order = normalizeOrderFromWire(record.order);
	if (order) {
		record.order = order;
	} else {
		delete record.order;
	}
}

function parseAwsJson(value: unknown): unknown {
	if (typeof value === 'string') {
		const trimmed = value.trim();
		if (!trimmed) {
			return undefined;
		}
		try {
			return JSON.parse(trimmed) as unknown;
		} catch {
			return undefined;
		}
	}
	return value;
}

interface CubeLeafFilterRow {
	member: string;
	operator: string;
	values?: unknown;
	dataType?: unknown;
}

function isCubeLeafFilterRow(value: unknown): value is CubeLeafFilterRow {
	if (!value || typeof value !== 'object' || Array.isArray(value)) {
		return false;
	}
	const record = value as Record<string, unknown>;
	return (
		String(record.member ?? '').trim().length > 0 &&
		String(record.operator ?? '').trim().length > 0
	);
}

/** Flattens Cube.js `{ and: [...] }` / `{ or: [...] }` trees into leaf filter rows. */
function collectCubeLeafFilterRows(
	node: unknown,
	out: Record<string, unknown>[]
): void {
	if (!node) {
		return;
	}
	if (Array.isArray(node)) {
		for (const item of node) {
			collectCubeLeafFilterRows(item, out);
		}
		return;
	}
	if (typeof node !== 'object') {
		return;
	}
	const record = node as Record<string, unknown>;
	if (isCubeLeafFilterRow(record)) {
		out.push(record as Record<string, unknown>);
		return;
	}
	if (Array.isArray(record.and)) {
		collectCubeLeafFilterRows(record.and, out);
	}
	if (Array.isArray(record.or)) {
		collectCubeLeafFilterRows(record.or, out);
	}
	if (Array.isArray(record.filters)) {
		collectCubeLeafFilterRows(record.filters, out);
	}
}

/** Maps `generateDataFilter.filter` (AWSJSON) into editor `FilterConfig[]`. */
export function parseGeneratedDataFilterAwsJson(raw: unknown): FilterConfig[] {
	const parsed = parseAwsJson(raw);
	if (!parsed) {
		return [];
	}

	if (Array.isArray(parsed)) {
		return normalizeCardDataFiltersFromApi(parsed) ?? [];
	}

	if (typeof parsed === 'object') {
		const record = parsed as Record<string, unknown>;
		if (Array.isArray(record.filters)) {
			return normalizeCardDataFiltersFromApi(record.filters) ?? [];
		}
		if (isCubeLeafFilterRow(record)) {
			return normalizeCardDataFiltersFromApi([record]) ?? [];
		}
		const leaves: Record<string, unknown>[] = [];
		collectCubeLeafFilterRows(parsed, leaves);
		if (leaves.length > 0) {
			return normalizeCardDataFiltersFromApi(leaves) ?? [];
		}
	}

	return [];
}

/** Maps dropdown `data.selected` to `data.filters` before stripping `selected`. */
function mapDropdownSelectedToFilters(data: Record<string, unknown>): void {
	const selected = data.selected;
	if (!Array.isArray(selected) || selected.length === 0) {
		return;
	}
	const dimensions = data.dimensions;
	let member = '';
	if (
		Array.isArray(dimensions) &&
		dimensions[0] &&
		typeof dimensions[0] === 'object'
	) {
		member = String(
			(dimensions[0] as Record<string, unknown>).name ?? ''
		).trim();
	}
	if (!member) {
		return;
	}

	const existing = Array.isArray(data.filters)
		? (data.filters as unknown[])
		: [];
	const rest = existing.filter(
		entry =>
			!(
				entry &&
				typeof entry === 'object' &&
				(entry as Record<string, unknown>).member === member
			)
	);
	rest.push({
		member,
		operator: 'equals',
		values: JSON.stringify(selected),
	});
	data.filters = rest;
}

function filterValuesToAwsJson(values: unknown): string {
	if (typeof values === 'string') {
		const trimmed = values.trim();
		return trimmed.length > 0 ? trimmed : '[]';
	}
	if (Array.isArray(values)) {
		return JSON.stringify(values);
	}
	return '[]';
}

/** Serializes `data.filters` for AppSync `CardDataConfigFilterInput.values` (AWSJSON). */
export function serializeCardDataFiltersForGql(
	filters: unknown
): Array<Record<string, unknown>> | undefined {
	if (!Array.isArray(filters) || filters.length === 0) {
		return undefined;
	}
	const out: Array<Record<string, unknown>> = [];
	for (const row of filters) {
		if (!row || typeof row !== 'object') {
			continue;
		}
		const record = row as Record<string, unknown>;
		const member = String(record.member ?? '').trim();
		const operator = String(record.operator ?? '').trim();
		if (!member || !operator) {
			continue;
		}
		const serialized: Record<string, unknown> = {
			member,
			operator,
			values: filterValuesToAwsJson(record.values),
		};
		if (record.dataType !== undefined && record.dataType !== null) {
			serialized.dataType = record.dataType;
		}
		out.push(serialized);
	}
	return out.length > 0 ? out : undefined;
}

/**
 * Removes client/runtime-only keys from a card `data` patch before GraphQL `updateCard`.
 *
 * Metric `aggregation` is an override only — omit when it matches the analytics schema
 * default (the data tab enforces this on emit; do not add schema defaults here).
 */
export function sanitizeCardDataConfigForGql(
	data: Record<string, unknown>
): Record<string, unknown> {
	const out = { ...data };
	for (const key of CARD_DATA_RUNTIME_ONLY_KEYS) {
		delete out[key];
	}

	const isDropdownFilterData =
		Array.isArray(out.dimensions) &&
		out.dimensions.length > 0 &&
		!Array.isArray(out.metrics);

	if (DROPDOWN_FILTER_GQL_PERSIST_VIA_SELECTED && isDropdownFilterData) {
		delete out.filters;
	} else {
		mapDropdownSelectedToFilters(out);

		if (typeof out.filters !== 'string') {
			const serializedFilters = serializeCardDataFiltersForGql(
				out.filters
			);
			if (serializedFilters) {
				out.filters = JSON.stringify(serializedFilters);
			} else {
				delete out.filters;
			}
		}

		for (const key of CARD_DATA_CLIENT_ONLY_KEYS) {
			delete out[key];
		}
	}

	if (Array.isArray(out.dimensions)) {
		out.dimensions = out.dimensions.map(sanitizeMetricDimensionForGql);
	}
	if (Array.isArray(out.metrics)) {
		out.metrics = out.metrics.map(sanitizeMetricDimensionForGql);
	}

	const timeDimension = out.timeDimension;
	if (
		timeDimension &&
		typeof timeDimension === 'object' &&
		!Array.isArray(timeDimension)
	) {
		const td = { ...(timeDimension as Record<string, unknown>) };
		for (const key of TIME_DIMENSION_RUNTIME_KEYS) {
			delete td[key];
		}
		if (td['dateRange'] == null) {
			delete td['dateRange'];
		}
		out.timeDimension = td;
	}
	return out;
}

/** Resolves dropdown default selection from `data.selected` or persisted `data.filters`. */
export function resolveDropdownFilterSelectedFromData(data?: {
	dimensions?: Array<{ name?: string }>;
	selected?: string[];
	filters?: Array<{ member?: string; operator?: string; values?: unknown }>;
}): string[] | undefined {
	const saved = data?.selected?.filter(v => v.trim().length > 0);
	if (saved && saved.length > 0) {
		return saved;
	}
	const member = data?.dimensions?.[0]?.name?.trim();
	if (!member || !data?.filters?.length) {
		return undefined;
	}
	const filter = data.filters.find(f => f.member?.trim() === member);
	if (!filter) {
		return undefined;
	}
	const values = parseFilterValuesAwsJson(filter.values);
	return values.length > 0 ? values : undefined;
}

/**
 * True when `data.filters` is only the dropdown default-selection equals filter
 * for the card dimension (legacy persistence via `selected` → `filters`).
 */
export function isDropdownDefaultSelectionPersistedFilter(data?: {
	dimensions?: Array<{ name?: string }>;
	filters?: unknown;
}): boolean {
	const member = data?.dimensions?.[0]?.name?.trim();
	if (!member) {
		return false;
	}
	const filters = normalizeCardDataFiltersFromApi(data?.filters);
	if (!filters || filters.length !== 1) {
		return false;
	}
	const row = filters[0];
	return row.member === member && row.operator === 'equals';
}
